import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/firebase/admin';
import { Button } from '../common/Button';

interface AffiliateApplication {
  id: string;
  fullName: string;
  email: string;
  website: string;
  promotionIntention: string;
  createdAt: any;
  status: string;
}

export const AffiliateApprovalList: React.FC = () => {
  const [applications, setApplications] = useState<AffiliateApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const apps = await adminService.getPendingAffiliateApplications();
      setApplications(apps);
    } catch (error) {
      console.error('Failed to load applications', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleApprove = async (id: string) => {
    if (!window.confirm('Are you sure you want to approve this affiliate?')) return;
    setProcessingId(id);
    try {
      await adminService.approveAffiliate(id);
      setApplications(prev => prev.filter(app => app.id !== id));
    } catch (error) {
      alert('Failed to approve affiliate');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason === null) return; // Cancelled

    setProcessingId(id);
    try {
      await adminService.rejectAffiliate(id, reason);
      setApplications(prev => prev.filter(app => app.id !== id));
    } catch (error) {
      alert('Failed to reject affiliate');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading applications...</div>;
  }

  if (applications.length === 0) {
    return <div className="p-4 text-center text-neutral-500">No pending affiliate applications.</div>;
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-neutral-900">Pending Affiliate Applications</h3>
        <p className="mt-1 max-w-2xl text-sm text-neutral-500">Review and approve new affiliate requests.</p>
      </div>
      <ul className="divide-y divide-neutral-200">
        {applications.map((app) => (
          <li key={app.id} className="px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-indigo-600 truncate">{app.fullName}</h4>
                <p className="text-sm text-neutral-500">{app.email}</p>
                {app.website && (
                  <p className="text-sm text-neutral-500">
                    Website: <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{app.website}</a>
                  </p>
                )}
                <div className="mt-2">
                  <p className="text-xs text-neutral-400 uppercase tracking-wider">Promotion Plan:</p>
                  <p className="text-sm text-neutral-700 mt-1">{app.promotionIntention}</p>
                </div>
                <p className="mt-2 text-xs text-neutral-400">
                  Applied on: {app.createdAt?.toDate ? app.createdAt.toDate().toLocaleDateString() : 'Unknown'}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0 flex space-x-2">
                <Button 
                  size="sm" 
                  variant="primary" 
                  onClick={() => handleApprove(app.id)}
                  loading={processingId === app.id}
                  disabled={!!processingId}
                >
                  Approve
                </Button>
                <Button 
                  size="sm" 
                  variant="danger" 
                  onClick={() => handleReject(app.id)}
                  loading={processingId === app.id}
                  disabled={!!processingId}
                >
                  Reject
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
